import { Menu } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { RouteNavigationProgress } from '../../../components/navigation/RouteNavigationProgress';
import { NonAdminPreviewBanner } from '../../../components/NonAdminPreviewBanner';
import { useAuthContext } from '../../../context/AuthContext';
import { UiProvider } from '../../../ui/ui-provider';
import {
  RecognitionAdminAccessProvider,
  useRecognitionAdminAccess,
} from '../../context/RecognitionAdminAccessContext';
import { useForgeTheme } from '../../hooks/useForgeTheme';
import { useMobileNavDrawer } from '../../hooks/useMobileNavDrawer';
import { useSidebar } from '../../hooks/useSidebar';
import {
  FORGE_CLEANING_TOOL_PATH,
  FORGE_MODEL_COMPARISON_PATH,
  FORGE_PR_REVIEW_PATH,
  FORGE_RECOGNITION_OKRS_PATH,
  FORGE_RECOGNITION_REPLAY_PATH,
  FORGE_REMOTE_DC_TOOL_PATH,
  FORGE_SYSTEM_HEALTH_PATH,
} from '../../paths';
import '../../styles/forge-theme.css';
import { getForgePageLabel } from './forgeNavigation';
import { ForgeSidebar } from './ForgeSidebar';
import { ThemeToggle } from './ThemeToggle';

// Full-width labeling workspaces: the sidebar starts hidden so the image and inspector get the
// whole viewport, and the reveal button brings it back.
const SIDEBAR_AUTO_HIDDEN_PATHS = [
  FORGE_CLEANING_TOOL_PATH,
  FORGE_REMOTE_DC_TOOL_PATH,
  FORGE_PR_REVIEW_PATH,
];

function getAutoHiddenSidebarPath(pathname: string): string | null {
  return (
    SIDEBAR_AUTO_HIDDEN_PATHS.find(
      (path) => pathname === path || pathname.startsWith(`${path}/`)
    ) ?? null
  );
}

// Wide enough for the longest destination name ("Recognition Thresholds",
// 114px of text). The names are what identify a page, so the default width has
// to show them whole; at the shared 138 the sidebar clipped that one.
const FORGE_SIDEBAR_WIDTH = 168;

const ForgeLayoutContent: React.FC = () => {
  const { hasInternalAccess, hasRemoteDataCollectionAccess } = useAuthContext();
  const { hasRecognitionAdminAccess } = useRecognitionAdminAccess();
  const { theme, toggleTheme } = useForgeTheme();
  const {
    width,
    isCollapsed,
    isHidden,
    isResizing,
    toggleCollapse,
    hideSidebar,
    showSidebar,
    onDragStart,
  } = useSidebar(FORGE_SIDEBAR_WIDTH);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    isMobile,
    isOpen: isMobileSidebarOpen,
    open: openMobileSidebar,
    close: closeMobileSidebar,
    openerRef: mobileNavButtonRef,
    drawerRef: mobileSidebarShellRef,
  } = useMobileNavDrawer(location.pathname);
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);
  const [revealedAutoHiddenPath, setRevealedAutoHiddenPath] = useState<string | null>(null);
  const pageLabel = useMemo(() => getForgePageLabel(location.pathname), [location.pathname]);
  const autoHiddenSidebarPath = useMemo(
    () => getAutoHiddenSidebarPath(location.pathname),
    [location.pathname]
  );
  const isSidebarAutoHidden =
    Boolean(autoHiddenSidebarPath) && revealedAutoHiddenPath !== autoHiddenSidebarPath;
  const isRecognitionAdminPath = [
    FORGE_RECOGNITION_OKRS_PATH,
    FORGE_RECOGNITION_REPLAY_PATH,
    FORGE_MODEL_COMPARISON_PATH,
    FORGE_PR_REVIEW_PATH,
  ].some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));
  const canShowForgeNavigation =
    hasInternalAccess || hasRecognitionAdminAccess === true || hasRemoteDataCollectionAccess;
  const labelerOnlyNavigation = !hasInternalAccess;

  useEffect(() => setRevealedAutoHiddenPath(null), [location.pathname]);

  useEffect(() => {
    if (isRecognitionAdminPath && hasRecognitionAdminAccess === false) {
      navigate(FORGE_SYSTEM_HEALTH_PATH, { replace: true });
    }
  }, [hasRecognitionAdminAccess, isRecognitionAdminPath, navigate]);

  const handleShowSidebar = () => {
    if (autoHiddenSidebarPath) setRevealedAutoHiddenPath(autoHiddenSidebarPath);
    showSidebar();
  };

  const desktopSidebarVisible =
    canShowForgeNavigation && !isMobile && !isHidden && !isSidebarAutoHidden;
  const sidebarOffset = desktopSidebarVisible ? (isCollapsed ? 38 : width) : 0;

  return (
    <div className="forge-root forge-shell" data-forge-theme={theme} ref={setPortalContainer}>
      <UiProvider container={portalContainer}>
        <RouteNavigationProgress />
        <div className={`forge-layout ${isMobile ? 'is-mobile-shell' : ''}`}>
          {canShowForgeNavigation && isMobile ? (
            <div className="forge-mobile-topbar">
              <button
                type="button"
                ref={mobileNavButtonRef}
                className="forge-mobile-nav-button"
                aria-label="Open navigation"
                aria-expanded={isMobileSidebarOpen}
                onClick={openMobileSidebar}
              >
                <Menu />
              </button>
              <div className="forge-mobile-title">{pageLabel}</div>
              <ThemeToggle theme={theme} onToggle={toggleTheme} variant="topbar" />
            </div>
          ) : null}

          {desktopSidebarVisible || (canShowForgeNavigation && isMobile) ? (
            <div
              ref={mobileSidebarShellRef}
              tabIndex={-1}
              className={`forge-sidebar-shell ${isMobileSidebarOpen ? 'is-mobile-open' : ''}`}
            >
              {isMobile ? (
                <button
                  type="button"
                  className="forge-sidebar-backdrop"
                  aria-label="Close navigation"
                  data-testid="forge-mobile-sidebar-backdrop"
                  onClick={closeMobileSidebar}
                />
              ) : null}
              <ForgeSidebar
                theme={theme}
                width={width}
                isCollapsed={isCollapsed}
                isResizing={isResizing}
                isMobile={isMobile}
                toggleCollapse={toggleCollapse}
                hideSidebar={hideSidebar}
                onDragStart={onDragStart}
                toggleTheme={toggleTheme}
                currentPath={location.pathname}
                hasRecognitionAdminAccess={hasRecognitionAdminAccess === true}
                hasRemoteDataCollectionAccess={hasRemoteDataCollectionAccess}
                recognitionOnly={labelerOnlyNavigation}
                onMobileNavigate={isMobile ? closeMobileSidebar : undefined}
              />
            </div>
          ) : null}
          {canShowForgeNavigation && !isMobile && (isHidden || isSidebarAutoHidden) ? (
            <button
              type="button"
              className="forge-sidebar-reveal-button"
              aria-label="Show navigation"
              title="Show navigation"
              onClick={handleShowSidebar}
            >
              <Menu />
            </button>
          ) : null}
          <main
            className={`forge-main-content ${isResizing ? 'sidebar-resizing' : ''}`}
            style={{ marginLeft: sidebarOffset }}
          >
            <NonAdminPreviewBanner />
            {isRecognitionAdminPath && hasRecognitionAdminAccess !== true ? (
              <div className="dg-loading-state" role="status">
                Checking Recognition access...
              </div>
            ) : (
              <Outlet />
            )}
          </main>
        </div>
      </UiProvider>
    </div>
  );
};

export const ForgeLayout: React.FC = () => (
  <RecognitionAdminAccessProvider>
    <ForgeLayoutContent />
  </RecognitionAdminAccessProvider>
);
